// Rend un ViolationDetail en une phrase lisible, chiffres compris.
//
// Trois écrans (TradingAccountsPage, AccountsTableWidget, DayDetailModal)
// recopiaient le même `if (tp === ...) return t....` et affichaient tous le
// même libellé nu : « Règle de consistance violée », sans le pourcentage
// atteint ni le seuil. Sur un compte à 3 trades, ce message ressemble à un bug
// alors que la règle est réellement franchie — impossible de trancher sans le
// chiffre.
//
// Centralisé ici pour que les trois écrans disent la MÊME chose, et pour
// qu'ajouter un type de violation n'oblige plus à penser à trois fichiers.

import type { ViolationDetail } from "./violationLabels";

export interface ViolationLabelStrings {
  dailyLossViolation: string;
  dailyLossWarning: string;
  consistencyViolation: string;
  consistencyWarning: string;
  /** Variantes chiffrées, avec {share}, {threshold} et {date}. */
  consistencyViolationDetail: string;
  consistencyWarningDetail: string;
}

/** Formate un pourcentage sans décimale superflue : 34 %, 34,5 %. */
function fmtPercent(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

/**
 * Libellé d'une violation, chiffré quand les données le permettent.
 *
 * RÉTROCOMPATIBILITÉ : quand `daySharePercent` ou le seuil manquent (payload
 * produit par un serveur antérieur à ce correctif), on retombe sur le libellé
 * nu historique plutôt que d'écrire « NaN% ».
 */
export function formatViolationLabel(
  detail: ViolationDetail,
  labels: ViolationLabelStrings,
  formatDate: (isoDate: string) => string = (d) => d,
): string {
  const isConsistency = detail.type.startsWith("consistency");
  const isViolation = detail.severity === "violation";

  if (!isConsistency) {
    return isViolation ? labels.dailyLossViolation : labels.dailyLossWarning;
  }

  const canDetail =
    detail.daySharePercent !== null && detail.thresholdPercent !== null;

  if (!canDetail) {
    return isViolation ? labels.consistencyViolation : labels.consistencyWarning;
  }

  const template = isViolation
    ? labels.consistencyViolationDetail
    : labels.consistencyWarningDetail;

  return template
    .replace("{share}", fmtPercent(detail.daySharePercent as number))
    .replace("{threshold}", fmtPercent(detail.thresholdPercent as number))
    .replace("{date}", formatDate(detail.date));
}
